import './OfflineInputs.scss';
import { useMemo, Dispatch, SetStateAction, ReactNode } from 'react';
import { MdDelete, MdEdit, MdOutlineUploadFile, MdSave } from 'react-icons/md';
import { HiOutlinePlus } from 'react-icons/hi';
import { format } from 'date-fns';
import cn from 'classnames';
import { OfflineInputTabs } from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar/FilterBar';
import { Spinner } from '@Components/Spinner';
import { DownloadToggle } from '@Components/DownloadDropdown/DownloadToggle';
import {
  Appearance,
  Background,
  Design,
  Placement,
  Size,
  StretchMode
} from '@Components/designConstants';
import { transformEmailToFullName } from '@Components/MetricsTable/metrics-table-utils';
import { DateRange } from '../../../utils/date-utils';
import { SetError } from '../../../redux/modals';
import { EmptyInputs } from './EmptyInputs';
import { OrderByOptions } from './OfflineInputsUtils';

const SEARCH_BAR_WIDTH = 260;

interface PromosSubHeaderProps {
  editingUnlocked: boolean;
  orderBy: OrderByOptions;
  setOrderBy: Dispatch<SetStateAction<OrderByOptions>>;
  timelineDateRange: DateRange;
  setTimelineDateRange: Dispatch<SetStateAction<DateRange>>;
}

const PromosSubHeader = ({
  editingUnlocked,
  orderBy,
  setOrderBy,
  timelineDateRange,
  setTimelineDateRange
}: PromosSubHeaderProps) => (
  <div className='tables-sub-header'>
    <Dropdown
      overlayPlacement={Placement.RIGHT}
      size={Size.LG}

      label='Sort by'
      value={orderBy}
      options={[OrderByOptions.DATE_ASCENDING, OrderByOptions.ALPHABETICAL]}
      onChange={val => setOrderBy(val)}
      stretch={StretchMode.FIT}
    />
    <DatePicker
      background={Background.LIGHT}
      design={editingUnlocked ? Design.NEUTRAL : Design.BRAND}
      range={timelineDateRange}
      onChange={newDates => setTimelineDateRange(newDates)}
      appearance={Appearance.FILLED}
      datePresets
      removeHeaderText
      isOutsideRange={() => false}
    />
  </div>
);

interface InputContainerProps {
  dialogs: ReactNode;
  tables: ReactNode;
  inputType: keyof typeof OfflineInputTabs;
  updated: { by?: string; at?: string };
  isEmpty: boolean;
  fetching: boolean;
  fetchFailed: boolean;
  refetch: () => void;
  submitting: boolean;
  editingUnlocked: boolean;
  orderBy?: OrderByOptions;
  timelineDateRange?: DateRange;
  hasUnsavedChanges: boolean;
  setEditingUnlocked: Dispatch<SetStateAction<boolean>>;
  onFilter?: (filter: (line: Record<string, unknown>) => boolean) => void;
  validateAndUpdateTables: () => void;
  setShowAddInputModal: Dispatch<SetStateAction<boolean>>;
  setShowDiscardDialog: Dispatch<SetStateAction<boolean>>;
  // Part of the shared caller contract; not consumed by this container directly.
  // eslint-disable-next-line react/no-unused-prop-types
  setError: SetError;
  download: () => void;
  setOrderBy?: Dispatch<SetStateAction<OrderByOptions>>;
  setTimelineDateRange?: Dispatch<SetStateAction<DateRange>>;
  setShowImportModal?: Dispatch<SetStateAction<boolean>>;
}

export const InputContainer = ({
  dialogs,
  tables,
  inputType,
  updated,
  isEmpty,
  fetching,
  fetchFailed,
  refetch,
  submitting,
  editingUnlocked,
  orderBy,
  timelineDateRange,
  hasUnsavedChanges,
  setEditingUnlocked,
  onFilter,
  validateAndUpdateTables,
  setShowAddInputModal,
  setShowDiscardDialog,
  download,
  setOrderBy,
  setTimelineDateRange,
  setShowImportModal
}: InputContainerProps) => {
  const updatedLabel: ReactNode = useMemo(() => {
    const { by, at } = updated;

    try {
      const atDate = new Date(at ?? '');
      const atStrFormatted = format(atDate, 'MM/dd/yyyy');

      return `Last updated on ${atStrFormatted} by ${transformEmailToFullName(by ?? '')}`;
    } catch {
      // If "at" cannot be parsed as a date, display nothing.
      return null;
    }
  }, [updated]);

  const addMediaLabel: string = useMemo(() => {
    switch (OfflineInputTabs[inputType]) {
      case OfflineInputTabs.PAID_MEDIA_INPUTS:
        return 'Paid Media Input';
      case OfflineInputTabs.NON_PAID_MEDIA_INPUTS:
        return 'Non-Paid Media Input';
      case OfflineInputTabs.OUTCOME_DATA:
        return 'Outcome Data';
      case OfflineInputTabs.PROMOS:
        return 'Promos';
      case OfflineInputTabs.CONTROL_VARIABLES:
        return 'Control Variable';
      case OfflineInputTabs.INCREMENTALITY_TESTING:
        return 'Incrementality Test';
      default:
        return '';
    }
  }, [inputType]);

  const isPromos =
    OfflineInputTabs[inputType] === OfflineInputTabs.PROMOS &&
    Boolean(orderBy) &&
    Boolean(setOrderBy) &&
    Boolean(timelineDateRange) &&
    Boolean(setTimelineDateRange) &&
    Boolean(setShowImportModal);

  return (
    <>
      {dialogs}
      <div className='offline-inputs-group'>
        {fetching && <Spinner />}
        {!fetching && isEmpty && (
          <EmptyInputs
            inputType={addMediaLabel}
            showAddMediaInputModal={() => setShowAddInputModal(true)}
            openImportDataModal={setShowImportModal ? () => setShowImportModal(true) : undefined}
            hasError={fetchFailed}
            onRetry={refetch}
          />
        )}
        {!fetching && !isEmpty && (
          <>
            <div className='tables-header'>
              <span className='tables-header-updated-by'>{updatedLabel}</span>
              <span className='tables-actions'>
                {editingUnlocked && onFilter && (
                  <FilterBar
                    size={Size.LG}
                    hasAdvanced={false}
                    onFilter={onFilter}
                    options={['date']}
                    width={SEARCH_BAR_WIDTH}
                  />
                )}
                {editingUnlocked ? (
                  <Button
                    size={Size.LG}
                    design={Design.NEUTRAL}
                    disabled={submitting}
                    onClick={() => setShowAddInputModal(true)}
                  >
                    <HiOutlinePlus />
                    Add {addMediaLabel}
                  </Button>
                ) : (
                  <Button size={Size.LG} onClick={() => setEditingUnlocked(true)}>
                    <MdEdit />
                    Edit
                  </Button>
                )}
                {setShowImportModal && editingUnlocked && (
                  <Button
                    size={Size.LG}
                    design={editingUnlocked ? Design.NEUTRAL : Design.BRAND}
                    leadingElement={<MdOutlineUploadFile />}
                    onClick={() => {
                      setShowImportModal(true);
                    }}
                  >
                    Bulk Upload
                  </Button>
                )}
                <DownloadToggle
                  design={editingUnlocked ? Design.NEUTRAL : Design.BRAND}
                  isOpen={false}
                  onToggleClick={download}
                />
              </span>
            </div>
            {isPromos &&
              orderBy &&
              setOrderBy &&
              timelineDateRange &&
              setTimelineDateRange &&
              setShowImportModal && (
                <PromosSubHeader
                  editingUnlocked={editingUnlocked}
                  orderBy={orderBy}
                  setOrderBy={setOrderBy}
                  timelineDateRange={timelineDateRange}
                  setTimelineDateRange={setTimelineDateRange}
                />
              )}
            <div
              className={cn('tables-container', {
                locked: !editingUnlocked,
                'timeline-chart': isPromos
              })}
            >
              {tables}
            </div>
            {editingUnlocked && (
              <div className='tables-footer'>
                <div className='tables-actions'>
                  <Button
                    size={Size.LG}
                    design={Design.NEUTRAL}
                    disabled={submitting || !hasUnsavedChanges}
                    onClick={() => setShowDiscardDialog(true)}
                  >
                    <MdDelete />
                    Discard Edits
                  </Button>
                  <Button
                    size={Size.LG}
                    disabled={submitting || !hasUnsavedChanges}
                    loading={submitting}
                    onClick={validateAndUpdateTables}
                  >
                    <MdSave />
                    Commit
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
